import { tr } from "./tr";
import { en } from "./en";

export { BRAND, type Brand } from "./brand";

export type Locale = "tr" | "en";
export const LOCALES: Locale[] = ["tr", "en"];
export const DEFAULT_LOCALE: Locale = "tr";

type Dict = { [k: string]: string | Dict };
const catalogs: Record<Locale, Dict> = { tr: tr as unknown as Dict, en: en as unknown as Dict };

export type Params = Record<string, string | number | null | undefined>;

function lookup(dict: Dict, key: string): string | undefined {
  let cur: string | Dict | undefined = dict;
  for (const part of key.split(".")) {
    if (cur === undefined || typeof cur === "string") return undefined;
    cur = cur[part];
  }
  return typeof cur === "string" ? cur : undefined;
}

const pluralRules: Partial<Record<Locale, Intl.PluralRules>> = {};
function plural(locale: Locale, n: number): string {
  pluralRules[locale] ??= new Intl.PluralRules(locale);
  return pluralRules[locale]!.select(n);
}

/**
 * Çeviri. Anahtar yoksa varsayılan dile (tr), o da yoksa anahtarın kendisine düşer.
 * Çoğul: `count` parametresi varsa önce `anahtar_one` / `anahtar_other` aranır.
 */
export function translate(locale: Locale, key: string, params: Params = {}): string {
  const count = params.count;
  let template: string | undefined;
  if (typeof count === "number") {
    const form = plural(locale, count);
    template = lookup(catalogs[locale], `${key}_${form}`) ?? lookup(catalogs[locale], `${key}_other`);
  }
  template ??= lookup(catalogs[locale], key) ?? lookup(catalogs[DEFAULT_LOCALE], key);
  if (template === undefined) return key;
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const v = params[name];
    return v === undefined || v === null ? "" : typeof v === "number" ? formatNumber(locale, v) : v;
  });
}

export function hasKey(locale: Locale, key: string): boolean {
  return lookup(catalogs[locale], key) !== undefined;
}

export function formatNumber(locale: Locale, n: number): string {
  return new Intl.NumberFormat(locale === "tr" ? "tr-TR" : "en-GB", { maximumFractionDigits: 2 }).format(n);
}

export function formatMoney(locale: Locale, minor: number, currency: string): string {
  return new Intl.NumberFormat(locale === "tr" ? "tr-TR" : "en-GB", { style: "currency", currency }).format(minor / 100);
}

/** "YYYY-AA-GG" → "8 Ekim 2026 Perşembe" gibi; saat dilimi kaymasız. */
export function formatDate(
  locale: Locale,
  date: string,
  style: "long" | "medium" | "short" | "weekday" | "dayMonth" = "medium",
): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  const opts: Intl.DateTimeFormatOptions =
    style === "long"
      ? { day: "numeric", month: "long", year: "numeric", weekday: "long" }
      : style === "medium"
        ? { day: "numeric", month: "long", year: "numeric" }
        : style === "weekday"
          ? { day: "numeric", month: "long", weekday: "long" }
          : style === "dayMonth"
            ? { day: "numeric", month: "long" }
            : { day: "2-digit", month: "2-digit", year: "numeric" };
  return new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-GB", { ...opts, timeZone: "UTC" }).format(dt);
}

export function formatDateRange(locale: Locale, from: string, to: string): string {
  if (from === to) return formatDate(locale, from, "medium");
  const [y1, m1] = from.split("-");
  const [y2, m2] = to.split("-");
  if (y1 === y2 && m1 === m2) {
    const day1 = Number(from.slice(8));
    return `${day1}–${formatDate(locale, to, "medium")}`;
  }
  if (y1 === y2) return `${formatDate(locale, from, "dayMonth")} – ${formatDate(locale, to, "medium")}`;
  return `${formatDate(locale, from, "medium")} – ${formatDate(locale, to, "medium")}`;
}

export function formatDateTime(locale: Locale, iso: string, timeZone = "Europe/Istanbul"): string {
  return new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-GB", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(new Date(iso));
}

/** Gün/saat miktarı: "3 gün", "1,5 gün", "2 saat". */
export function formatAmount(locale: Locale, amount: number, unit: "day" | "hour"): string {
  return translate(locale, unit === "day" ? "units.days" : "units.hours", { count: amount });
}

export function createT(locale: Locale) {
  return (key: string, params?: Params) => translate(locale, key, params);
}
export type TFunction = ReturnType<typeof createT>;
